/**
 * Every API path the client knows, in one place.
 *
 * Nothing else in the app may write a URL string. When the real APIs land the
 * only thing that changes here is the path text; call sites, query keys and
 * fixtures all stay put. Paths are relative — the base URL comes from
 * VITE_API_URL via the axios instance.
 */

export const API_VERSION = 'v1'

const v = (path) => `/${API_VERSION}${path}`

export const endpoints = {
  /* ── Identity ─────────────────────────────────────────────────────────
     These two are LIVE — they are the only paths in this file the real API
     actually serves, and the transport routes them to the network rather than
     to the fixture layer. Everything below is still mock-backed.

     There is deliberately no `logout` entry: the API exposes no such endpoint,
     and its refresh tokens are stateless JWTs that nothing can revoke, so a
     server round trip would have no effect to wait for. Signing out clears the
     client's cookies — see features/auth/api.js.

     `me` is the profile read. Sign-in used to return the user on the login
     payload; it no longer does, so who is signed in comes from here instead —
     an authenticated GET, made straight after the tokens are stored. It also
     carries the account, the roster shifts and the permission map, which is
     more than the login response ever described. */
  auth: {
    login: v('/auth/login'),
    /* The SESSION read — user, account, roster shifts and the permission map.
       It is what the shell needs to render, and it is deliberately not the
       profile record: it carries no id and no email. See `users.me`. */
    me: v('/auth/me'),
    refresh: v('/auth/refresh'),
    /* Asks for a reset link. It answers the SAME 200 and the same message
       whether or not that account and address exist — never revealing which
       addresses have accounts — so nothing that calls it may branch on the
       response to say "sent" or "not found". See features/auth/api.js.

       The token it mails is a `set_password` token, the same type an invitation
       carries, which is why one screen serves both arrivals. */
    forgotPassword: v('/auth/forgot-password'),
  },

  /* ── Users ────────────────────────────────────────────────────────────
     LIVE. `me` is the PROFILE record — the same person as `auth.me` describes,
     read and written as an editable row rather than as a session. It is the one
     that serves `id`, `email` and `avatar_file_id`, and the one the profile form
     PUTs back. Two endpoints for one person is not duplication: the shell needs
     a session on every load, and the form needs a record only when it opens.

     `list` and `detail` are the ACCOUNT DIRECTORY, and they are the same path
     the demo directory below has always used — `/v1/users` is one resource, and
     it is real now. Which is why this is not a split namespace the way
     `/v1/assets` is: there, the live entries name their own sub-resources and
     the demo register at the bare path keeps falling through. Here the live
     collection IS the bare path, so the two cannot coexist by prefix. They
     coexist by MODE instead — with `VITE_AUTH_MODE=mock` the transport takes
     every live path back to fixtures, which is what `verify:mocks` runs in and
     why `people` below still answers there. See http.js.

     Both are behind `users.manage`. A root user (`is_root`) is LISTED and
     nothing more: `detail`, `update` and `delete` all refuse one, because a root
     account is managed through `me` and never from here.

     `setPassword` and `confirmEmail` are PUBLIC — no Authorization header, a
     token from an email instead. `setPassword` serves both the invite flow's
     first password and the forgot-password reset, with the same token type
     either way, so one screen is correct for both. */
  users: {
    me: v('/users/me'),
    list: v('/users'),
    detail: (id) => v(`/users/${id}`),
    setPassword: v('/users/set-password'),
    confirmEmail: v('/users/confirm-email'),
  },

  /* ── MCP authorisation ────────────────────────────────────────────────
     LIVE. The consent step of the OAuth flow that connects an MCP client
     (Claude, Claude Code) to ForgeAI. The backend runs the protocol; these two
     only show the pending request and carry back the signed-in user's answer.
     Both take the signed `token` the backend put in the page's URL, and both
     are authenticated — approving is acting as the person signed in. */
  mcp: {
    preview: v('/mcp/preview'),
    decision: v('/mcp/decision'),
  },

  /* ── Account ──────────────────────────────────────────────────────────
     LIVE. `timezones` is reference data for any form carrying a display
     preference, and it is the ONLY correct source for that list: the API
     validates a submitted zone against `zoneinfo.available_timezones()` and
     this endpoint returns that same set. The browser's own
     `Intl.supportedValuesOf('timeZone')` is a DIFFERENT list — it holds
     canonical names only, while the server's set depends on the tzdata its
     runtime was built with — so a zone picked from one can be refused by the
     other. It was, on `Asia/Calcutta`. */
  account: {
    /* The organisation record — GET and PUT, both behind `account.manage`.
       Three editable fields and no more: `alias`, `org_name`, `logo_file_id`.
       Everything else Settings → General used to offer is commented out there
       rather than deleted, because it is the design's page and the fields may
       yet land. */
    current: v('/account'),
    timezones: v('/account/timezones'),
  },

  /* ── Files ────────────────────────────────────────────────────────────
     LIVE. Uploads are a three-call handshake, and the middle call is not one
     of these: the browser PUTs the bytes straight to object storage at a
     presigned address the first call hands back. Only the two ends are API
     routes.

       uploadUrl  POST  → a `file_id` and a five-minute presigned PUT address
       confirm    POST  → the server fetches the object back and inspects it
       viewUrl    GET   → a presigned read address, for an ATTACHED file only

     `confirm` is the one to read carefully. A file that fails inspection still
     answers 200, with `status: 'rejected'` and a reason in the body — so the
     caller must branch on the payload, never on the HTTP code. See
     features/files/api.js, which is the only place that should know this. */
  files: {
    /* The resource prefix, for the transport's live-path list only — nothing
       calls it. `uploadUrl` cannot serve as that prefix: `/files/{id}/confirm`
       does not start with `/files/upload-url`, so half the handshake would have
       fallen through to the fixture layer and 404'd. */
    root: v('/files'),
    uploadUrl: v('/files/upload-url'),
    confirm: (fileId) => v(`/files/${fileId}/confirm`),
    viewUrl: (fileId) => v(`/files/${fileId}/view-url`),
  },
  org: {
    current: v('/org'),
    /* DEMO. The General page. GET reads the settings with the options they are
       chosen from — the sites from the hierarchy, the landing pages from the
       rail — and PATCH changes them. */
    settings: v('/org/settings'),
    /* DEMO. The danger zone's two actions that do something. `reset` is a POST
       because it is a command rather than a field with a new value, and `export`
       is a GET because it takes nothing and changes nothing. There is no
       `DELETE /org`: see the page. */
    reset: v('/org/reset'),
    export: v('/org/export'),
    entitlements: v('/org/entitlements'),

    /* DEMO. Settings → Modules & Plan. `plan` is the same module map
       `entitlements` serves, with what each module is currently doing and the
       tier it sits in — one request, because the banner's counts are counts of
       the cards below it and fetching them apart would let them disagree.

       The write is spelled as the MODULE it changes, not as the page: this
       screen owns no records, and a toggle here is an entitlement change that
       the navigation rail reads on its next render. */
    plan: v('/org/plan'),
    module: (id) => v(`/org/modules/${id}`),

    /* DEMO. Settings → Billing & Usage. One read for the whole page: the plan,
       its meters, the invoice history, the card on file and the trend are all
       one billing period, and fetching them apart would let them describe
       different ones.

       `billingContact` is a PATCH on the billing record and is the ONLY write
       this page has. There is deliberately no payment-method endpoint — see the
       fixture. */
    billing: v('/org/billing'),
    billingContact: v('/org/billing/contact'),
  },

  /* ── Asset classes ────────────────────────────────────────────────────
     LIVE — the third path the real API serves. Account-scoped reusable types:
     a machine model, a line configuration, a site blueprint, a sensor type.
     Plant, Line and Machine each carry a nullable `asset_class_id` pointing
     back here, so a class is the template and the hierarchy holds instances.

     `list` is also the create path — the API POSTs to the collection. Updates
     are PUT, not PATCH: the API has no partial-patch semantics anywhere, and
     its update schema accepts `name` only, so `entity_type` is fixed once a
     class exists.

     The collection also accepts `?entity_type=<enum>` as a server-side filter.
     The screen deliberately does not use it: it reads the whole set once so
     the type rail can show a count per type, and narrows in the client. A
     filtered fetch per rail click would still need an unfiltered one behind it
     just to render those counts. */
  assetClasses: {
    list: v('/assets/classes'),
    detail: (id) => v(`/assets/classes/${id}`),
  },

  /* ── Asset hierarchy ──────────────────────────────────────────────────
     LIVE. Four tiers: enterprise › site › line › asset — the account, then the
     three real resources beneath it.

     ── It was six, and the two that went are not coming back ──────────────
     Migration `f915c8d4b697` dropped the `regions` and `subregions` tables,
     moved `plants` onto `account_id`, and stripped both labels from the
     `TargetLevel` enum. The backend has no router and no schema for either, so
     `/assets/regions` and `/assets/subregions` are 404s, not deprecations.
     They were listed here — and claimed in LIVE_PATHS — after that landed,
     which is what sent this screen's first drill-down to a route that no longer
     exists. A plant's parent is the account, which is not a path segment.

     `tree` drives the whole node list on its own: it returns the nested shape
     with `short_code` at every level, and `features/hierarchy/flatten.js`
     reshapes it. The collections are no longer read for the list.

     They are still here because `tree` carries neither `is_active` nor
     `asset_class_id`, and every update is a full-replace PUT that must send
     both — so an ancestor walk and a stored scope's node are read through the
     `detail` paths. See `features/hierarchy/api.js` `fetchNode`.

     The account is the root and has no endpoint at all: it cannot be created,
     renamed or removed from here, which is why the tree's top row is the one
     node with no controls on it. */
  hierarchy: {
    /* RETIRED 2026-10-13 — the whole-estate read; see `fetchHierarchyNodes`.
    tree: v('/assets/hierarchy'), */
    plants: v('/assets/plants'),
    plant: (id) => v(`/assets/plants/${id}`),
    lines: v('/assets/lines'),
    line: (id) => v(`/assets/lines/${id}`),
    machines: v('/assets/machines'),
    machine: (id) => v(`/assets/machines/${id}`),
  },

  /* ── Fixture-only hierarchy ───────────────────────────────────────────
     The demo universe, still behind the mock adapter. `/org` feeds the site
     health cards and the public page's site list, and the fixtures under it are
     keyed to fixture site ids — repointing them at the live tree would empty
     the whole demo, so the two coexist until the scoped endpoints land. */
  demoHierarchy: {
    tree: v('/hierarchy'),
    scopes: v('/hierarchy/scopes'),
    site: (siteId) => v(`/hierarchy/sites/${siteId}`),
    nodes: v('/hierarchy/nodes'),
  },

  /* ── Anomalies ────────────────────────────────────────────────────────
     LIVE, and NOT the same resource as `alerts` below.

     `alerts` is the demo universe: fixture-backed, denormalised, and carrying
     fields no endpoint serves (a reading, a threshold, a fusion score, an
     assignee). It still feeds the home screen, the public page and the
     assistant dock. `anomalies` is the real API — a narrower record with a
     hierarchy attachment — and it is what the Anomalies screen reads. The two
     coexist until the demo surfaces are moved over; nothing should be added to
     `alerts` from here on.

     `list` is paginated and is the ONLY endpoint that carries the facet counts:
     they arrive inside the list response as `counts.status` and
     `counts.severity`, so there is no separate counts request to make. See
     `fetchAnomalies`.

     Updates are **PUT**, and a FULL replace. `AnomalyUpdate` is `severity` and
     `status`, both required — a status change that omits the severity is a 422.
     `title` is NOT on it: an anomaly's title is the detector's, and the only
     thing that rewrites it is the copilot's `update_title` directive.

     ── The RCA sub-resource is GONE ───────────────────────────────────────
     There was a `/anomalies/{id}/rca` with its own row, its own status enum and
     a `rca/request` POST. None of it exists. The analysis is now `content` on
     the anomaly itself — a JSONB column — with `history` beside it as the
     timeline of changes to it, and both arrive on the ordinary detail read.

     Nothing this client can call writes them. `content` is written by exactly
     one path: the copilot dispatcher's `("anomalies", "update_content")`
     handler, fed from `POST /copilot/internal/turns/{id}/chunk`, which is
     engine-authenticated. Requesting an analysis is a copilot turn carrying
     `metadata.actions: [{type: 'request_rca', module: 'anomalies', module_id}]`
     — not wired here, because the copilot client is itself off-contract. See
     BACKEND-GAPS.md.

     rca: (anomalyId) => v(`/anomalies/${anomalyId}/rca`),
     rcaRequest: (anomalyId) => v(`/anomalies/${anomalyId}/rca/request`), */
  anomalies: {
    list: v('/anomalies'),
    /**
     * DEMO. The facets the list cannot count yet — snoozed, unassigned.
     *
     * `/anomaly-facets`, deliberately NOT `/anomalies/facets`. `isLivePath`
     * matches on the START of the path and `/v1/anomalies` is in `LIVE_PATHS`, so
     * anything nested under it goes to the network — where this route does not
     * exist and answers 404. A sibling path is the whole of the fix, and it is
     * worth knowing about before adding any other mock beneath a live prefix.
     */
    demoFacets: v('/anomaly-facets'),
    detail: (anomalyId) => v(`/anomalies/${anomalyId}`),

    /**
     * The anomaly's sensor-tag set. `PUT`, and a FULL REPLACE.
     *
     * ── The body is the whole target set, not a delta ───────────────────
     * `replace_sensor_tags` syncs the collection against what it is sent: a tag
     * in the body is created or updated, and a tag LEFT OUT is soft-deleted.
     * So a caller holding a stale copy does not fail to save a change — it
     * silently deletes every tag that arrived after its copy was taken. That is
     * why the mutation below rebuilds the body from the record it just read and
     * why `useAnomaly` holds no cache.
     *
     * ── Unpinning has two spellings and they mean different things ──────
     * `type: 'unpinned'` keeps the row and flags it; omitting the tag removes
     * the row. This client uses the second — see `saveAnomalySensorTags`.
     *
     * ── Adding a tag is checked harder than removing one ────────────────
     * `verify_sensor_tags` only checks the tags that are NEW to the set, and it
     * checks two things: the actor's `sensor_explorer.manage`/`use` grant, and
     * that the tag's sensor sits at EXACTLY the anomaly's
     * `(plant_id, line_id, machine_id)` — `verify_linked_sensor_tags` is called
     * without `beneath`, so a sensor on a machine under a line-level anomaly is
     * a 400. A tag already in the set stays removable regardless.
     */
    sensorTags: (anomalyId) => v(`/anomalies/${anomalyId}/sensor-tags`),

    /**
     * One pinned sensor tag's readings, for the Analysis tab's traces.
     *
     * ── The range is not a free parameter ───────────────────────────────
     * `from`/`to` default to the anomaly's own window and are REJECTED outside
     * it: `from` before `window_start` or `to` after `window_end + 1µs` is a
     * `ValidationAppError`, not a clamp. So this is a reader for the event, not
     * a general history endpoint — which is why the page never offers a range
     * control over it.
     *
     * ── It pages, and the client pages it LAZILY ────────────────────────
     * A full page (50 rows, `sensor_reading_history_page_size`) comes back with
     * `to` pulled in to just past the last reading; that `to` is the next
     * request's `from`. A SHORT page means the window is exhausted, and its `to`
     * is the window end unchanged — the only difference between the two
     * responses is the row count, so the length test is the cursor logic.
     *
     * `useSensorTagReadings` fetches one page on mount and the next only when
     * somebody pans to the end of what is loaded. It used to loop to exhaustion
     * up front: eight requests a card, thirty-two to open a board of four, for
     * readings mostly nobody looked at. Ignoring the paging altogether is the
     * other failure and is worse — it truncates every trace at 50 points, which
     * looks like a sensor that stopped reporting rather than a client that
     * stopped asking, so the chart says when it is holding a partial window.
     */
    sensorTagReadings: (anomalyId, sensorTagId) =>
      v(`/anomalies/${anomalyId}/sensor-tags/${sensorTagId}/readings`),

    /* ── Three SSE streams ────────────────────────────────────────────────
       All three authenticate from `?token=` rather than a header — the server's
       own accommodation for `EventSource`, and `token` is a REQUIRED query
       param on them, so there is no header path to choose instead. The token is
       appended by `services/sse.js`, not spelled here.

       `stream` pushes one changed LIST ITEM per event, account-wide.
       `detailStream` pushes the whole `AnomalyOut` for one anomaly — and wakes
       on its child rows too (sensor tags, similarities, document references),
       which is what makes the detail screen's linked records live.

       `sensorTagStream` is declared and deliberately NOT used. The Analysis
       tab's traces come from `sensorTagReadings` instead, and the reason is the
       window: this stream pushes readings as they ARRIVE, while the readings
       route reads `[window_start, window_end]`. An anomaly whose window closed
       — which is nearly all of them by the time anybody opens one — would hold
       four live connections open to receive nothing. It is the right endpoint
       for an anomaly still firing, and that is a different screen.

       Capped server-side at `max_concurrent_streams_per_user` (4) in its own
       `anomaly_sensor_tags` budget, separate from the sensor chart's. Whatever
       opens these has to cap its cards to match. */
    stream: v('/anomalies/stream'),
    detailStream: (anomalyId) => v(`/anomalies/${anomalyId}/stream`),
    sensorTagStream: (anomalyId, sensorTagId) =>
      v(`/anomalies/${anomalyId}/sensor-tags/${sensorTagId}/stream`),
  },

  /* ── Monitor ──────────────────────────────────────────────────────── */
  alerts: {
    list: v('/alerts'),
    detail: (alertId) => v(`/alerts/${alertId}`),
    counts: v('/alerts/counts'),
    acknowledge: (alertId) => v(`/alerts/${alertId}/acknowledge`),
    analysis: (alertId) => v(`/alerts/${alertId}/analysis`),
  },
  assets: {
    list: v('/assets'),
    /* Bulk create-or-update. A collection POST would be the obvious spelling and
       is wrong here: this is not "create an asset", it is "reconcile these
       rows against the plant", and it answers with how many of each it did. */
    import: v('/assets/import'),
    detail: (assetId) => v(`/assets/${assetId}`),
    documents: (assetId) => v(`/assets/${assetId}/documents`),
    health: (assetId) => v(`/assets/${assetId}/health`),
  },
  /* `list` and `detail` are LIVE — create, list, read, update and delete are all
     served. `PUT` is NARROWER than `POST`: it takes neither the parent trio nor
     `sensor_connection_id`, so a sensor cannot be re-attached or re-pointed
     after onboarding. See features/sensors/api.js `updateSensor`.

     `waveformTypes` and `valueUnits` are LIVE too, and are platform master data
     rather than anything belonging to a sensor — global, read-only, seeded, the
     same arrangement as `connectorTypes`. They are mounted under this router
     because a sensor is the only thing that references them, and both are
     REQUIRED on create and update, so neither form can be filled without them.

     Their literal paths sit above `detail`'s parameterised one: FastAPI matches
     in declaration order and the router declares them first, so
     `/sensors/waveform-types` never reaches `get_sensor` as a malformed UUID.
     Order here is documentation of that, not what causes it.

     There is deliberately no `series` entry. One existed, pointing at
     `/sensors/{id}/series`, and it was a trap rather than a placeholder: this
     whole prefix is in LIVE_PATHS, so it had no fixture to fall back on and the
     API has never served that route — the first caller would have got a 404 from
     the network. Nothing called it.

     The timeseries the UI will eventually want is a different resource anyway:
     the backend serves it from `/sensors/charts` — `POST`, `GET`, `PUT`, `DELETE`
     and an SSE `/charts/{id}/stream`, keyed by chart rather than by sensor.
     Adding it means adding that resource, not restoring this path. */
  sensors: {
    list: v('/sensors'),
    waveformTypes: v('/sensors/waveform-types'),
    valueUnits: v('/sensors/value-units'),
    detail: (sensorId) => v(`/sensors/${sensorId}`),
  },
  /* Sensor charts. LIVE — a saved set of sensor TAGS attached to one plant, line
     or machine, shared account-wide. Behind `sensor_explorer.manage` or
     `sensor_explorer.use`, not the `sensors.*` keys the sensor list needs.

     Mounted at `/sensors/charts`, which the API router declares BEFORE the
     sensor router so `/charts` never reaches `get_sensor` as a malformed UUID.
     Already covered by the `/v1/sensors` entry in LIVE_PATHS.

     There is NO `GET /sensors/charts/{id}`: the collection serves `POST` and
     `GET`, the id serves `PUT` and `DELETE`, and a single chart is read off the
     list. See `features/sensorCharts/api.js` and BACKEND-GAPS.md.

     `readings` backs the detail screen's History view; `stream` (SSE, token in
     `?token=`, one `reading` event per value of any tag on the chart, and the
     last 60s replayed on connect) backs its Live view. The stream's tag set is
     fixed when it connects, so a tag change means a new connection. */
  sensorCharts: {
    list: v('/sensors/charts'),
    detail: (chartId) => v(`/sensors/charts/${chartId}`),
    readings: (chartId) => v(`/sensors/charts/${chartId}/readings`),
    stream: (chartId) => v(`/sensors/charts/${chartId}/stream`),
  },
  oee: {
    summary: v('/oee/summary'),
    byLine: v('/oee/lines'),
  },

  /* ── Lines & Assets · DEMO ────────────────────────────────────────────
     `/production-lines` and not `/lines`, which is already taken: the real API
     serves `/v1/lines` as the hierarchy's line collection and it is in
     LIVE_PATHS, so anything registered under that prefix goes to the network.

     They are genuinely different resources. `hierarchy.lines` is the structural
     record — a name, a parent, an asset class — and this one is the operational
     view of the same line: state, throughput, health, the assets on it and their
     open alerts. The demo composes the second from the first plus a fixture
     overlay, and when the real endpoint lands it will do the same server-side.

     `assets.detail` below already existed and is reused for the station screen
     rather than duplicated here — one asset, one path. */
  productionLines: {
    list: v('/production-lines'),
    detail: (lineId) => v(`/production-lines/${lineId}`),
  },
  /* ── Sensor Explorer · DEMO ───────────────────────────────────────────
     `/signals` and not `/sensors`, which is already taken and is LIVE. They are
     genuinely different resources and the split is the same one as
     `/production-lines` beside `/lines`.

     A SENSOR is the configuration of a tag: its name, its waveform type, its
     value unit, which connection reads it, what its extraction config says. That
     is what the real API serves and what Data Fusion → IoT sensors edits.

     A SIGNAL is the measured channel: a normal operating band, a current
     reading, a state and a trace over a window. Nothing serves it yet.

     `scopes` is the hierarchy above the channels with a count on every node. It
     rides beside the collection rather than inside it because the selector must
     keep showing the whole plant while the grid is narrowed to one line — a
     count that changed when you filtered would be answering a different
     question. */
  signals: {
    list: v('/signals'),
    scopes: v('/signals/scopes'),
    detail: (signalId) => v(`/signals/${signalId}`),
  },

  /* ── Timeseries Workstation · DEMO ────────────────────────────────────
     A chart is a saved set of channels with a name on it. `list` is also the
     create path — the New Chart dialog POSTs to the collection.

     `detail` takes the span as query params (`window` or `from`/`to`, plus
     `granularity`) and answers the chart with its traces, statistics,
     correlations and events already resolved. That is deliberate rather than
     lazy: the statistics panel, the series table and the AI banner are three
     views of one computation, and computing it three times in the client is how
     they come to disagree.

     `adhoc` is the id the Workstation uses when it was opened from a channel
     card rather than from a saved chart — the channels arrive in `?signals=`
     and nothing is stored. It is a path segment and not a separate route so
     that one screen serves both, and saving an ad-hoc chart is a POST to the
     collection rather than a different page. */
  charts: {
    list: v('/charts'),
    detail: (chartId) => v(`/charts/${chartId}`),
  },

  /* ── Notifications · DEMO ─────────────────────────────────────────────
     Alert rules, the channels they deliver through, and the escalation ladder,
     all on ONE response. They are three views of one configuration and the
     interesting facts are the joins between them — a live rule pointing at an
     unconfigured channel is only visible if you hold both at once.

     `rule` serves PATCH, which is the enable/disable toggle and nothing else:
     the rest of a rule is an edit, and an edit is the dialog. */
  notifications: {
    list: v('/notifications'),
    rule: (id) => v(`/notifications/rules/${id}`),
    rules: v('/notifications/rules'),
    /* DEMO. A personal delivery setting, not an org routing rule — which is why
       it is its own sub-resource rather than a field on the configuration. */
    preference: (id) => v(`/notifications/preferences/${id}`),
    channel: (id) => v(`/notifications/channels/${id}`),
    channelTest: (id) => v(`/notifications/channels/${id}/test`),
    escalation: v('/notifications/escalation'),
  },

  /* ── Executive Dashboard · DEMO ───────────────────────────────────────
     One response: the site rollups, the enterprise totals and the AI brief.
     They are three views of one computation — the brief names the site the
     bars show as worst, and the totals are summed off the same rows — so
     splitting them would be three chances to disagree. */
  executive: {
    summary: v('/executive'),
  },

  /* ── Analytics & Reports · DEMO ───────────────────────────────────────
     The generated-report shelf. `list` is also the generate path — a POST
     starts one, which then takes time to finish, so the collection is polled
     while anything on it is still running. */
  reports: {
    list: v('/reports'),
    detail: (id) => v(`/reports/${id}`),
    /* The standard-report catalogue: templates, their categories and personas,
       the sections a build can be assembled from, and the delivery schedule.
       Separate from `list` because it is a catalogue, not a collection of the
       user's things — it never changes while the tab is open, so it is fetched
       once and never polled, while `list` polls whenever a build is running. */
    library: v('/reports/library'),
  },

  /* ── Logbook · LIVE ───────────────────────────────────────────────────
     The whole resource — `POST`, `GET` (collection and detail), `PUT` and
     `DELETE` — behind `logbook.manage` or `logbook.record_entries`.

     Every filter is a server-side query param: `category`, `severity`, the
     parent trio, `asset_type`/`asset_id`, `anomaly_id`, `work_order_id`,
     `shift_id` and the `shift_date_from`/`shift_date_to` window. The collection
     also answers `counts.category` and `counts.severity`, taken BEFORE the
     category and severity filters apply — the same arrangement the work-order
     board's column headers use, so a chip narrows the feed without rewriting
     the totals beside it.

     ── What the DEMO collection had and this one does not ──────────────────
     `q` (expanded over a synonym table, answering with the terms that earned a
     match), `cited=true`, and the `assets`/`plant`/`author`/`shift` lists that
     rode along on the response. None is a route the API serves. The pickers now
     read the resources that own them — the hierarchy and Shift Master — which
     is the shape `WorkOrdersPage` already uses. Recorded in BACKEND-GAPS.md.

     The two `stream` entries are SSE, token in the query string, and push a
     changed row per event: `stream` account-wide (a list item, filtered to the
     reader's scopes), `detailStream` for one entry (the full detail). Neither
     announces a delete — see `features/logbook/liveStream.js`. */
  logbook: {
    list: v('/logbook'),
    detail: (id) => v(`/logbook/${id}`),
    stream: v('/logbook/stream'),
    detailStream: (id) => v(`/logbook/${id}/stream`),
  },

  /* ── Documents & SOPs · LIVE ──────────────────────────────────────────
     A TOP-LEVEL resource at `/v1/documents`, not a Data Fusion sub-path. The
     screen lives under Data Fusion and the collection does not — `endpoints
     .dataFusion.documents` was the demo's spelling and is retired below.

     Create, list, read, update and delete. `PUT` is a FULL replace: name, type,
     both dates, all five attachment lists AND `document_file_id`. Sending a
     different file id is how a new revision is made — the server adds
     revision N+1 rather than overwriting, so a document's history is append
     only and every revision keeps its own view URL.

     The create also takes `plant_id` (the OWNER plant, null = whole account)
     and `level`; both are Create-only and the update refuses them.

     Reads take any of the four documents keys. Create and delete take
     `documents.manage`; the update takes manage or either attach key, and
     without a manage grant over the owner plant only the attachments change.

     There is NO route for a revision's status — `/documents/internal/revisions/
     {id}/status` is the AI engine's, behind `verify_engine`, and is not part of
     this app's surface. The status arrives on the document read. */
  documents: {
    list: v('/documents'),
    detail: (id) => v(`/documents/${id}`),
  },

  /* ── Shifts ───────────────────────────────────────────────────────────
     Two different resources, and they are kept apart by PATH. That is the whole
     reason to read this block.

     `patterns` is LIVE — shift TEMPLATES, behind Settings → Shift Master. A
     pattern is a name, a type, a window, the weekdays it runs and the ONE plant
     it belongs to; `plant_id: null` is an account-level pattern applying to
     every plant, which is a real value and not an omission. It was `plant_ids`,
     a list, until the `shift_scopes` table was dropped. Full CRUD at
     `/v1/shifts`, behind `shifts.manage`.

     `list` is DEMO and is the ROSTER — the shifts actually worked, who worked
     them, on which line. `current` is the shift being worked right now. A
     template is not an occurrence of it, so these are not one resource and
     cannot be merged.

     ── They used to share `/v1/shifts`, and it did not hold ───────────────
     The roster was there first and the real API mounted templates on top of it.
     They were split by MODE rather than by path: the roster answered from
     fixtures only under `VITE_AUTH_MODE=mock`. That holds exactly as long as
     nothing under the prefix is called in live mode, and three things were —
     `LIVE_PATHS` claims `/v1/shifts` by PREFIX, so the roster's own query, the
     top bar's `current`, and a demo POST all reached the real server.

     None of them failed usefully. The roster sent `?type=all`, which the
     template endpoint parses against an enum and rejects; `/shifts/current` does
     not exist, so it fell through to `/shifts/{shift_id}` and 422'd trying to
     read "current" as a UUID, behind a chip that renders only when it has data.
     A 404 was the predicted outcome and would have been the kinder one.

     So: sibling paths, which is the same fix `anomalies.demoFacets` documents a
     few hundred lines up. Nothing demo-only may sit beneath a live prefix. */
  shifts: {
    /* LIVE. Also the create path — the API POSTs to the collection. */
    patterns: v('/shifts'),
    pattern: (id) => v(`/shifts/${id}`),
    /* DEMO, and deliberately NOT under `/shifts`. See the header.

       One collection, and the AI synopsis rides on it rather than having a route
       of its own: the briefing's every figure is counted off the same rows the
       table lists, and a second request would be a second chance for the two to
       disagree. */
    list: v('/shift-roster'),
    current: v('/shift-current'),
  },

  /* ── Shift handovers · LIVE ───────────────────────────────────────────
     `/v1/shifts/handovers`, full CRUD behind `shift_handovers.manage` or
     `shift_handovers.prepare`. Already inside `LIVE_PATHS` by the `/v1/shifts`
     prefix, so it needs no entry of its own there.

     Not to be confused with `handovers` further down, which is the DEMO archive
     at `/v1/handovers*` that the home card and the Shifts screen still read.
     That one has drafts, publishing and acknowledgement; this one has none of
     the three.

     The two `stream` entries are SSE, token in the query string, and push a
     changed row per event: `stream` account-wide (a list item), `detailStream`
     for one handover (the full detail). Neither announces a delete — see
     `features/shiftHandovers/liveStream.js`. */
  shiftHandovers: {
    list: v('/shifts/handovers'),
    detail: (id) => v(`/shifts/handovers/${id}`),
    stream: v('/shifts/handovers/stream'),
    detailStream: (id) => v(`/shifts/handovers/${id}/stream`),
  },

  /* ── Act ────────────────────────────────────────────────────────────
     DEMO. The collection is also the create path — a POST to `/work-orders`
     raises one, which is the ordinary spelling and is right here: this really
     is "create a work order", unlike `/assets/import`, which reconciles rows
     against a plant and answers with how many of each it did.

     `detail` serves GET, PATCH and DELETE. PATCH and not PUT: advancing a state
     and editing a title touch different fields, and a full replace would make
     the board's Move button send back a whole order to change one word. It is
     also the only place in this app that VALIDATES a transition — the handler
     checks the move against `WO_NEXT` and answers 422 for one the workflow does
     not allow, so a draft cannot be marked complete without being approved. */
  workOrders: {
    list: v('/work-orders'),
    detail: (id) => v(`/work-orders/${id}`),
    create: v('/work-orders'),
    /* SSE, token in the query string, behind `work_orders.manage` or
       `.update_assigned`. `stream` is account-wide (a list item, filtered to
       the reader's scopes), `detailStream` one order (the full `WorkOrderOut`).
       Neither announces a delete — see `features/workOrders/liveStream.js`. */
    stream: v('/work-orders/stream'),
    detailStream: (id) => v(`/work-orders/${id}/stream`),
  },

  /* ── Agents ───────────────────────────────────────────────────────── */
  /* `messages` serves GET and POST. GET is the DOCK's thread, grounded on
     ALR-4471 and unchanged; POST is the Copilot screen asking a question and is
     the ordinary spelling — asking really is creating a message.

     `prompts` is the screen's opening state: the four agent modes, their chips,
     and what the assistant says it is grounded to. Separate from `messages`
     because it is read once when the screen mounts and never again, while
     `messages` is written on every question. */
  copilot: {
    /* LIVE, and the whole of what the server serves. Five routes, no more:
       these four plus `turnStream`.

       ── Asking is TWO requests, not one ───────────────────────────────────
       `turns` is an ordinary JSON POST that answers 201 with the created row.
       It does NOT stream. The answer arrives on `turnStream`, opened
       afterwards against the id that POST returned. A client that expects SSE
       from the POST gets a JSON body and an empty conversation, which is what
       this file described for months.

       `turnStream` authenticates from `?token=` and only from there — the
       route depends on `get_current_user_from_query_token`, which declares
       `token` with no default, so there is no header path to choose instead.
       The token is appended by `services/sse.js`, not spelled here.

       A turn requires a session, and nothing creates one implicitly:
       `copilot_session_id` is required on the POST and a missing one is
       refused. `sessions` is where that comes from.

       ── `sessions` is searchable by TITLE and by nothing else ─────────────
       `?search=` is an ILIKE over the title column. There is no filter for
       "turns about this anomaly" anywhere in the API, so the title is the only
       per-anomaly index that exists — see `features/copilot/session.js` and
       BACKEND-GAPS.md. Sessions are scoped to their creator, not the account. */
    turns: v('/copilot/turns'),
    turnStream: (id) => v(`/copilot/turns/${id}/stream`),
    sessions: v('/copilot/sessions'),
    sessionTurns: (id) => v(`/copilot/sessions/${id}/turns`),
    /* Demo fixtures behind the public page. Unrelated to the four above. */
    messages: v('/copilot/messages'),
    prompts: v('/copilot/prompts'),
    session: v('/copilot/session'),
  },

  /* ── Data Fusion ──────────────────────────────────────────────────────
     LIVE, all of it — and mounted UNDER sensors, at `/v1/sensors/connections`.
     The resource reads as its own thing on this screen and in this file; the API
     nests it, because a connection is what a sensor is read through. Only the
     path reflects that, nothing else.

     `connectorTypes` sits under the connections prefix because that is where the
     router mounts it, not because it belongs to one connection. It is listed
     after `connection` so the more specific literal path is easy to see beside
     the parameterised one — order here is documentation only, the transport
     matches on the prefix.

     Updates are PUT, not PATCH, and replace all three editable fields:
     `plant_id` and `sensor_connector_type_id` are not updatable at all, so a connection
     cannot move site or change protocol.

     `connectors` below is unrelated: it feeds the public page's source-contract
     section from the demo fixtures and predates the real endpoints. Both stay. */
  dataFusion: {
    connections: v('/sensors/connections'),
    connection: (id) => v(`/sensors/connections/${id}`),
    connectorTypes: v('/sensors/connections/connector-types'),
    connectionTest: v('/sensors/connections/test'),

    connectors: v('/data-fusion/connectors'),

    /* DEMO. The other four source families — OT, vision, business systems,
       databases — which the product does not have endpoints for yet.

       Deliberately NOT under `/connections`: that prefix is in `LIVE_PATHS` and
       every request beneath it goes to the network. "Sources" is also the right
       noun for the wider set, since a document store and an S3 prefix are not
       connections in the sense that resource means. */
    sources: v('/data-fusion/sources'),

    /* DEMO. One source, with the things a table has no room for: the ingest
       contract, the config schema with its values resolved, and the activity
       log. A separate request from the collection rather than a fatter row —
       the config schema for a historian is thirteen fields and nobody reading
       a list of six sources needs seventy-eight of them.

       `source` serves GET and PATCH; `sourceTest` is a POST to an action
       sub-resource, for the reason the pipelines pass gives: "test this now" is
       not a field with a new value. */
    source: (id) => v(`/data-fusion/sources/${id}`),
    sourceTest: (id) => v(`/data-fusion/sources/${id}/test`),

    /* DEMO. One write-back rule. A sub-resource rather than a field on the
       source, because a rule is a record in its own right — it has a target
       object and its own enabled state, and folding six of them into the config
       payload would make the connection form responsible for something it does
       not show. */
    sourceWriteback: (id) => v(`/data-fusion/sources/${id}/writeback`),

    /* DEMO. The connect wizard. Both are deliberately OUTSIDE `/sources/` rather
       than `/sources/catalogue` and `/sources/probe`: those would be matched by
       `/sources/:id` on any router that takes the first pattern that fits, and a
       collection whose ids can collide with its own sub-paths is a collection
       waiting to serve a catalogue as a source.

       The probe is a POST because it reaches out to somewhere — and it is a probe
       of a CONNECTOR, not of a source, because at that point in the wizard there
       is no source yet. Testing one that exists is `sourceTest`. */
    connectorCatalogue: v('/data-fusion/connector-catalogue'),
    connectorProbe: v('/data-fusion/connector-probe'),

    /* DEMO. The module's landing screen. One request rather than five: every
       figure on it is a count taken across the same collections in the same
       pass, and five endpoints would let the cards disagree with each other by
       being fetched at different moments. */
    overview: v('/data-fusion/overview'),

    /* RETIRED. The document registry moved to `endpoints.documents` — the API
       serves it at `/v1/documents`, as a resource of its own rather than one of
       this module's sub-paths. The screen stays here; the collection does not.

       Kept commented rather than deleted, like the fixture routes behind them:
       `fusionDocs.js` still feeds the Overview's knowledge counts, the media
       registry and Asset Detail's Documents tab, none of which is live.

       documents: v('/data-fusion/documents'),
       document: (id) => v(`/data-fusion/documents/${id}`), */

    /* DEMO. Photographs, CAD, thermal captures and inspection video. A separate
       collection from documents rather than a `?kind=` on it: they share a
       linking model and nothing else — no revision, no page count, no
       verification, and a grid instead of a register. */
    media: v('/data-fusion/media'),

    /* DEMO. Contextualisation — the queue of objects that arrived without a
       home. A decision is a POST to the item rather than a PATCH of the record,
       because what is stored is the REVIEW, and accepting one writes a link into
       whichever registry the object came from. */
    context: v('/data-fusion/context'),
    contextDecision: (id) => v(`/data-fusion/context/${encodeURIComponent(id)}/decision`),

    /* DEMO. The knowledge graph. `graph` is the whole-estate counts plus the
       machines worth opening; `graphNode` is one machine's neighbourhood,
       positioned. Two requests because the picker is read once and the
       neighbourhood changes on every click. */
    graph: v('/data-fusion/graph'),
    graphNode: (id) => v(`/data-fusion/graph/${id}`),

    /* DEMO. Ingestion pipelines. Running and pausing are POSTs to an `actions`
       sub-resource rather than a PATCH of the record: "run this now" is not a
       field with a new value, and modelling it as one would mean inventing a
       field whose only job is to be written. */
    pipelines: v('/data-fusion/pipelines'),
    pipelineAction: (id) => v(`/data-fusion/pipelines/${id}/actions`),
  },

  /* ── Overview ─────────────────────────────────────────────────────── */
  briefing: {
    current: v('/briefing'),
  },

  /* ── Home dashboard · DEMO ────────────────────────────────────────────
     Fixture-backed and only read when `VITE_DEMO_MODE=true`. The paths are
     written the way the real API would serve them rather than under a `/demo`
     prefix, because the whole point of routing these through the transport is
     that landing the endpoint is a change of answer, not a change of address.

     `context` is the toolbar's site + shift line. `kpis` is the four-tile strip;
     two of its four values are computed from the alerts fixture by the handler
     so the strip cannot disagree with the list it links to. */
  home: {
    context: v('/home/context'),
    kpis: v('/home/kpis'),
    watched: v('/home/watched-assets'),
    assets: v('/home/my-assets'),
  },

  /* Tasks. A real resource in its own right — the home panel is one view of it,
     and Work Orders will be another. Create posts to the collection; update and
     delete take the id. */
  tasks: {
    list: v('/tasks'),
    detail: (id) => v(`/tasks/${id}`),
  },

  /* Shift handovers. `latest` is what the home card reads — the most recent
     published record, which is not always the newest row, because the newest may
     still be a draft. */
  handovers: {
    list: v('/handovers'),
    latest: v('/handovers/latest'),
    detail: (id) => v(`/handovers/${id}`),
    acknowledge: (id) => v(`/handovers/${id}/acknowledge`),
  },

  /* ── Insights ─────────────────────────────────────────────────────── */
  kpis: {
    topline: v('/kpis/topline'),
  },

  /* ── People · DEMO, and superseded ────────────────────────────────────
     SUPERSEDED BY `users.list`/`users.detail` ABOVE. Kept, not deleted, because
     it is the same path and still answers: with `VITE_AUTH_MODE=mock` the
     transport routes every live path back to the fixture layer, so these are the
     names `verify:mocks` and the fixture routes use, and `mocks/fixtures/
     people.js` still feeds five other demo modules. Nothing in `src/` calls
     them any more — Settings → Users reads `users.list`.

     The account directory behind Settings → Users. `/users` and not
     `/settings/users`: a person is a resource of the platform, not of the screen
     that happens to administer them, and the same collection is what the task
     assignee picker and the shift roster read from.

     `person` serves PATCH only, and the only field it takes is `status`. A name
     belongs to the person, a role to the roles page, a site to the hierarchy and
     a last sign-in to the log — offering any of them here would make this screen
     a second source of truth for a fact it does not own. */
  people: {
    list: v('/users'),
    detail: (id) => v(`/users/${id}`),
    /* Bulk create-or-update, and the same spelling the asset import uses: this
       is not "create a user", it is "reconcile these rows against the
       directory", and it answers with how many of each it did. */
    import: v('/users/import'),
    imports: v('/users/imports'),
  },

  /* DEMO. Developer access — API keys, webhooks and the published spec.
     One read, because the page is one page and the three lists are drawn
     together. A key's value is returned by `keys` on create and by
     `keyRotate`, and by nothing else ever. */
  developer: {
    access: v('/developer'),
    keys: v('/developer/keys'),
    key: (id) => v(`/developer/keys/${id}`),
    keyRotate: (id) => v(`/developer/keys/${id}/rotate`),
    webhooks: v('/developer/webhooks'),
    webhook: (id) => v(`/developer/webhooks/${id}`),
    webhookTest: (id) => v(`/developer/webhooks/${id}/test`),
    spec: v('/developer/spec'),
    postman: v('/developer/postman'),
  },

  /* DEMO. Settings → Integrations. One GET, because the connector grid and the
     pipeline monitor are two views of the same registry and fetching them
     separately would let them be fetched at different moments and disagree.

     The two writes are deliberately spelled as the SOURCE resource they change:
     this page has no records of its own, and an endpoint named for the page
     rather than for what it writes is how a second registry starts. */
  integrations: {
    list: v('/integrations'),
    sourcePause: (id) => v(`/integrations/sources/${id}/pause`),
    source: (id) => v(`/integrations/sources/${id}`),
  },

  /* DEMO. Security policy. GET reads it, PATCH changes it — and every accepted
     change writes to the audit log, which is how the two screens are joined. */
  security: {
    settings: v('/security'),
  },

  /* DEMO. The audit log. Read-only by construction: an audit log with a write
     endpoint is not an audit log. Entries are appended by the writes they
     record, in the fixtures that perform them. */
  audit: {
    list: v('/audit'),
  },

  /* ── Roles and permissions ────────────────────────────────────────────
     LIVE, and mounted under `/v1/users` — a role is a thing you grant a person,
     so the API nests it there. Both are behind `users.manage`.

     `permissions` is a seeded CATALOGUE and read-only: ten modules, each with
     its permissions, each permission naming the levels it can be granted at.
     There is no create, edit or delete, which is correct — a permission is a
     capability the code checks for, not a record anyone should be able to mint.

     `roles` is full CRUD, with three refusals worth knowing before they are
     hit: a system role (`is_system`) cannot be edited or deleted, a role
     somebody holds cannot have its permissions changed, and a role somebody
     holds cannot be deleted at all.

     Note what is NOT live: `/v1/users` itself is still the demo directory, so
     these two are listed individually in `LIVE_PATHS` rather than as a `/users`
     prefix that would swallow it. */
  roles: {
    list: v('/users/roles'),
    detail: (id) => v(`/users/roles/${id}`),
  },

  permissions: {
    list: v('/users/permissions'),
  },

  /* DEMO. Teams. A resource of its own and not `/users?group=`: a team has a
     lead, a channel and a default page, none of which belong to a person. */
  teams: {
    list: v('/teams'),
  },
}
